
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { LoginPage } from '@/components/Pages/Login/LoginPage';
import { LoginTokenPostPage } from '@/components/Pages/TransparentRedirectViews/LoginTokenPostPage';
import { getServerRuntimeConfig } from '@/config/runtime/serverRuntimeConfig';
import { HTTPMethod } from '@/services/Http/HttpClient';
import { parseRequestBody } from '@/utils/parseRequestBody';
import { APP_AUDIENCE, removeUndefined } from '@/utils/siteUtils';
import { fromLoginQuery, isPathSpecifyingDomain, isValidAbsoluteHttpsUrl, isValidPath } from '@/utils/urls';
import type { GetServerSidePropsContext } from 'next';
import type { ComponentProps } from 'react';
import { Component } from 'react';
const isDevBloombergHost = (host: string | string[] | undefined): boolean => {
    const hostValue = (Array.isArray(host) ? host[0] : host)?.split(':')[0]?.toLowerCase();
    return Boolean(hostValue?.endsWith('.dev.bloomberg.com'));
};
const isAllowedStackerRedirect = (url: string, host: string | string[] | undefined): boolean => {
    if (!isDevBloombergHost(host) || !isValidAbsoluteHttpsUrl(url)) {
        return false;
    }
    const parsed = new URL(url);
    return parsed.hostname.toLowerCase().endsWith('.stacker.dev.bloomberg.com');
};
const getSafeRedirectTarget = (url: string, host: string | string[] | undefined): string => {
    url = url.replace(/[\p{Cc}\p{Cf}]/gu, '');
    if (isValidPath(url) || (!!host && isAllowedStackerRedirect(url, host))) {
        return url;
    }
    return '/';
};
const sendUserToUrl = (url: string, host: string | string[] | undefined = undefined) => {
    return { redirect: { destination: getSafeRedirectTarget(url, host), statusCode: 302 } };
};
const getServerSideProps = async ({ req, res, query }: GetServerSidePropsContext) => {
    const { ServerHttpClient } = await import('@/services/Http/ServerHttpClient');
    const rawRedirectUrl = (query.r || query.state)?.toString() || '/';
    const redirectUrl = getSafeRedirectTarget(rawRedirectUrl, req?.headers?.host);
    const statusKey = query.status?.toString() || null;
    const api = new ServerHttpClient(req, res);
    let errorKey;
    // check if you have guest authentication token
    if (query.t) {
        return sendUserToUrl(`/login/guest?t=${query.t}&r=${encodeURIComponent(redirectUrl)}`);
    }
    if (redirectUrl.startsWith('/login')) {
        // Prevents login redirect loop
        return sendUserToUrl('/login?error=UNKNOWN_ERROR');
    }
    // check if you have sso/nefw token
    const token = query.token?.toString() || query.code?.toString() || null;
    if (token) {
        return {
            props: { loginTokenPostPageProps: { postUrl: '/login', token, redirectUrl } },
        };
    }
    // handle the form submit (POST)
    if (req?.method === HTTPMethod.POST) {
        let body;
        try {
            body = await parseRequestBody(req);
        }
        catch {
            errorKey = 'BAD_REQUEST';
        }
        if (body?.token && body?.r) {
            try {
                const result = await api.post({
                    path: '/auth/login',
                    body: {
                        code: body.token,
                        audience: `${APP_AUDIENCE} (${getServerRuntimeConfig().clientConfig.buildId})`,
                        userAgent: req.headers['user-agent'],
                        ip: req.headers['true-client-ip'] || req.headers['X-Forwarded-For'],
                    },
                });
                if (result.token) {
                    res.setHeader('set-cookie', result.cookies);
                    const returnUrl = getSafeRedirectTarget(body.r, req?.headers?.host);
                    return sendUserToUrl(returnUrl + (returnUrl.includes('?') ? '&' : '?') + fromLoginQuery + '=login', req?.headers?.host);
                }
                errorKey = result.message;
            }
            catch (e) {
                if (e?.response?.status === 400) {
                    errorKey = 'BAD_REQUEST';
                }
                else if (e?.response?.status === 401) {
                    errorKey = 'INVALID_TOKEN';
                }
                else if (e?.response?.status === 403) {
                    errorKey = 'INACTIVE';
                }
                else if (e?.message) {
                    errorKey = e.message;
                }
            }
        }
        else if (body?.email || body?.method === 'bsso') {
            try {
                const request = body?.email
                    ? {
                        path: '/auth/lookup',
                        body: {
                            email: body.email,
                            state: body?.state || '/',
                        },
                    }
                    : {
                        path: '/auth/bsso_init',
                        body: { state: body?.state || '/' },
                    };
                const response = await api.post(request);
                if (response?.url) {
                    if (response.cookies?.length) {
                        res.setHeader('set-cookie', response.cookies);
                    }
                    return { redirect: { destination: response.url, statusCode: 302 } };
                }
            }
            catch (e) {
                errorKey = 'UNKNOWN_ERROR';
                if (e?.response?.status === 400) {
                    errorKey = 'BAD_REQUEST';
                }
                else if (e?.message) {
                    errorKey = encodeURIComponent(e.message);
                }
            }
        }
        else {
            errorKey = 'BAD_REQUEST';
        }
    }
    // Stacker can't login by itself, redirect to Alpha
    if (req?.headers?.host?.endsWith('.stacker.dev.bloomberg.com') && !isPathSpecifyingDomain(redirectUrl)) {
        const r = encodeURIComponent(`https://${req.headers.host}${redirectUrl}`);
        return { redirect: { destination: `https://www.nefrpd.dev.bloomberg.com/login?r=${r}`, statusCode: 302 } };
    }
    if (query.error) {
        errorKey = query.error.toString();
    }
    if (query.expired) {
        errorKey = 'SESSION_EXPIRED';
    }
    if (req?.method === HTTPMethod.GET) {
        // For GET requests whose response isn't a redirect, the HTML page is always the same, so it can be cached
        // except when there's ?token=, ?code= query param, those are returned earlier before this line
        // This reduces server load when bots crawl the same URLs and all get redirected to login page
        // Caching for 1hr, same as public files (see next.config.js)
        res.setHeader('Cache-Control', 'public, max-age=3600');
    }
    return {
        props: { loginPageProps: removeUndefined({ postUrl: '/login', redirectUrl, errorKey, statusKey }) },
    };
};
class Page extends Component<{
    loginPageProps?: ComponentProps<typeof LoginPage>;
    loginTokenPostPageProps?: ComponentProps<typeof LoginTokenPostPage>;
}> {
    public render() {
        return (<>
                <HeadMeta title="Login"/>
                {this.props.loginTokenPostPageProps && <LoginTokenPostPage {...this.props.loginTokenPostPageProps}/>}
                {this.props.loginPageProps && <LoginPage {...this.props.loginPageProps}/>}
            </>);
    }
}
export default Page;

    async function __Next_Translate__getServerSideProps__1a1a25d204b__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/login',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a1a25d204b__ as getServerSideProps }
  